import clsx from 'clsx'
import { format } from 'date-fns'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, NavLink, useLocation } from 'react-router-dom'

import Avatar from '@/components-latest/primitives/Avatar'
import Button from '@/components-latest/primitives/Button'
import { ArrowLeft } from '@/components-latest/primitives/Icons'
import Timer from '@/components-latest/primitives/Icons/Timer'
import NotificationBell from '@/components-latest/primitives/NotificationBell'
import ScrollArea from '@/components-latest/primitives/Scrollable'
import SwitchWithWords from '@/components-latest/primitives/SwitchWithWords'
import SwitchWithWordsWithModal from '@/components-latest/primitives/SwitchWithWords/SwitchWithWordsWithModal'
import Text from '@/components-latest/primitives/Text'
import OnlineRestriction from '@/components-latest/ui/OnlineRestriction/OnlineRestriction'
import { PATHS } from '@/config/router/routes'
import { BUSY_STATUS, STORE_AVAILABILITY } from '@/constants/common'
import { selectIsSidebarOpen } from '@/ducks/app/selectors/app.selectors'
import { useAccessControl } from '@/ducks/auth/auth.permissions'
import { setSideBarOpen } from '@/ducks/app/slices/app.slice'
import { useGetOrdersCountByTypesQuery } from '@/ducks/orders/services/orders.api'
import {
  useChangeStoreAvailabilityMutation,
  useFetchStoreBranchesQuery,
  useGetStoreBusyStatusQuery
} from '@/ducks/store/api/store.api'
import {
  selectPhoneCode,
  selectStoreAvailability,
  selectStoreAvatar,
  selectStoreCashCoStatus,
  selectStoreId,
  selectStoreName,
  selectStoreType
} from '@/ducks/store/selectors/store.selectors'
import { setAutoBusy } from '@/ducks/store/slice/store.slice'
import { useDeviceSize } from '@/hooks/useDeviceSize/useDeviceSize'
import { logFirebaseEvent } from '@/utils/firebase.utils'

import Styles from './Sidebar.module.scss'

const FIGMA_CHEVRON_ICON = '/images/sidebar/chevron-down.svg'
const FIGMA_QUICK_SETTINGS_ICON = '/images/sidebar/quick-settings.svg'
const FIGMA_COLLAPSED_TOGGLE_ICON = '/images/sidebar/collapsed-toggle.svg'

const OrdersIcon = () => (
  <svg viewBox="0 0 18 20" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.7587 4.31291e-07H12.2413C13.0463 -1.23241e-05 13.7106 -2.28137e-05 14.2518 0.0441945C14.8139 0.0901197 15.3306 0.188684 15.816 0.435975C16.5686 0.819468 17.1805 1.43139 17.564 2.18404C17.8113 2.66937 17.9099 3.18608 17.9558 3.74817C18 4.28936 18 4.95372 18 5.75868V19C18 19.3761 17.7889 19.7204 17.4538 19.8911C17.1186 20.0618 16.716 20.03 16.4118 19.8087L14.2789 18.2575L12.3747 19.7809C12.0229 20.0623 11.5262 20.0738 11.1618 19.8087L9 18.2365L6.83817 19.8087C6.47378 20.0738 5.97715 20.0623 5.62531 19.7809L3.72111 18.2575L1.58817 19.8087C1.28399 20.03 0.881394 20.0618 0.546223 19.8911C0.211053 19.7204 9.08128e-07 19.3761 9.08128e-07 19L4.31291e-07 5.7587C-1.23241e-05 4.95373 -2.28137e-05 4.28937 0.0441945 3.74817C0.0901197 3.18608 0.188684 2.66937 0.435975 2.18404C0.819468 1.43139 1.43139 0.819468 2.18404 0.435975C2.66937 0.188684 3.18608 0.0901197 3.74818 0.0441945C4.28937 -2.28137e-05 4.95373 -1.23241e-05 5.7587 4.31291e-07ZM3.91104 2.03755C3.47262 2.07337 3.24842 2.1383 3.09202 2.21799C2.7157 2.40973 2.40973 2.7157 2.21799 3.09202C2.1383 3.24842 2.07337 3.47262 2.03755 3.91104C2.00078 4.36113 2 4.94342 2 5.8V17.0362L3.16183 16.1913C3.52623 15.9262 4.02285 15.9377 4.3747 16.2191L6.27889 17.7425L8.41183 16.1913C8.76248 15.9362 9.23752 15.9362 9.58817 16.1913L11.7211 17.7425L13.6253 16.2191C13.9771 15.9377 14.4738 15.9262 14.8382 16.1913L16 17.0362V5.8C16 4.94342 15.9992 4.36113 15.9625 3.91104C15.9266 3.47262 15.8617 3.24842 15.782 3.09202C15.5903 2.7157 15.2843 2.40973 14.908 2.21799C14.7516 2.1383 14.5274 2.07337 14.089 2.03755C13.6389 2.00078 13.0566 2 12.2 2H5.8C4.94342 2 4.36113 2.00078 3.91104 2.03755ZM13.2071 5.29289C13.5976 5.68342 13.5976 6.31658 13.2071 6.70711L8.70711 11.2071C8.31658 11.5976 7.68342 11.5976 7.2929 11.2071L5.29289 9.20711C4.90237 8.81658 4.90237 8.18342 5.29289 7.79289C5.68342 7.40237 6.31658 7.40237 6.70711 7.79289L8 9.08579L11.7929 5.29289C12.1834 4.90237 12.8166 4.90237 13.2071 5.29289Z"
    />
  </svg>
)

const PerformanceIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.9754 2.01031C11.8588 2.00078 11.6965 2.00001 11.4 2.00001H8.60001C8.30348 2.00001 8.14122 2.00078 8.02464 2.01031C8.02 2.01069 8.0156 2.01107 8.01145 2.01145C8.01107 2.0156 8.01069 2.02 8.01031 2.02464C8.00078 2.14122 8 2.30348 8 2.60001V4.99895L8.00001 5.00001L8 5.00106V18H12V2.60001C12 2.30348 11.9992 2.14122 11.9897 2.02464C11.9893 2.02 11.9889 2.0156 11.9886 2.01145C11.9844 2.01107 11.98 2.01069 11.9754 2.01031ZM14 8L14 2.56809C14 2.31571 14.0001 2.06993 13.9831 1.86178C13.9644 1.63318 13.9203 1.36345 13.782 1.09202C13.5903 0.715699 13.2843 0.409738 12.908 0.217992C12.6366 0.0796946 12.3668 0.0356271 12.1382 0.0169504C11.9301 -5.6861e-05 11.6843 -2.74161e-05 11.4319 2.50544e-06L8.56811 2.62465e-06C8.31572 -2.72969e-05 8.06993 -5.67418e-05 7.86178 0.0169505C7.63318 0.0356274 7.36345 0.0796949 7.09202 0.217992C6.7157 0.409739 6.40974 0.7157 6.21799 1.09202C6.07969 1.36345 6.03563 1.63318 6.01695 1.86178C5.99994 2.06993 5.99997 2.31572 6 2.56811C6 2.57873 6.00001 2.58936 6.00001 2.60001V4.00001H2.60001C2.58936 4.00001 2.57873 4 2.56811 4C2.31572 3.99997 2.06993 3.99994 1.86178 4.01695C1.63318 4.03563 1.36345 4.07969 1.09202 4.21799C0.7157 4.40974 0.409739 4.7157 0.217992 5.09202C0.0796947 5.36345 0.0356272 5.63318 0.0169504 5.86178C-5.6861e-05 6.06993 -2.74161e-05 6.31573 2.50544e-06 6.56811C3.81675e-06 6.57873 5.12805e-06 6.58936 5.12805e-06 6.60001L2.50544e-06 17.4319C-2.74161e-05 17.6843 -5.6861e-05 17.9301 0.0169504 18.1382C0.0356272 18.3668 0.0796948 18.6366 0.217992 18.908C0.409739 19.2843 0.7157 19.5903 1.09203 19.782C1.36345 19.9203 1.63318 19.9644 1.86178 19.9831C2.06993 20.0001 2.31571 20 2.56809 20H17.4319C17.6843 20 17.9301 20.0001 18.1382 19.9831C18.3668 19.9644 18.6366 19.9203 18.908 19.782C19.2843 19.5903 19.5903 19.2843 19.782 18.908C19.9203 18.6366 19.9644 18.3668 19.9831 18.1382C20.0001 17.9301 20 17.6843 20 17.4319V10.5681C20 10.3157 20.0001 10.0699 19.9831 9.86177C19.9644 9.63318 19.9203 9.36345 19.782 9.09202C19.5903 8.7157 19.2843 8.40974 18.908 8.21799C18.6366 8.07969 18.3668 8.03563 18.1382 8.01695C17.9301 7.99994 17.6843 7.99997 17.4319 8L14 8ZM14 10V18H17.4C17.6965 18 17.8588 17.9992 17.9754 17.9897C17.98 17.9893 17.9844 17.9889 17.9886 17.9885C17.9889 17.9844 17.9893 17.98 17.9897 17.9754C17.9992 17.8588 18 17.6965 18 17.4V10.6C18 10.3035 17.9992 10.1412 17.9897 10.0246C17.9893 10.02 17.9889 10.0156 17.9885 10.0115C17.9844 10.0111 17.98 10.0107 17.9754 10.0103C17.8588 10.0008 17.6965 10 17.4 10H14ZM6.00001 6.00001H2.60001C2.30348 6.00001 2.14122 6.00078 2.02464 6.01031C2.02 6.01069 2.0156 6.01107 2.01145 6.01145C2.01107 6.0156 2.01069 6.02 2.01031 6.02464C2.00078 6.14122 2.00001 6.30348 2.00001 6.60001V17.4C2.00001 17.6965 2.00078 17.8588 2.01031 17.9754C2.01069 17.98 2.01107 17.9844 2.01145 17.9885C2.0156 17.9889 2.02 17.9893 2.02464 17.9897C2.14122 17.9992 2.30348 18 2.6 18H6L6.00001 6.00001Z"
    />
  </svg>
)

const MenuIcon = () => (
  <svg viewBox="0 0 18.0002 22" aria-hidden="true">
    <g>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.5682 2.50544e-06C6.57882 3.81674e-06 6.58945 5.12804e-06 6.6001 5.12804e-06L11.432 2.50544e-06C11.6844 -2.74161e-05 11.9302 -5.6861e-05 12.1383 0.0169504C12.3669 0.0356272 12.6367 0.0796948 12.9081 0.217992C13.2844 0.409739 13.5904 0.715701 13.7821 1.09202C13.9204 1.36345 13.9645 1.63318 13.9832 1.86178C13.987 1.90871 13.99 1.95756 13.9923 2.00797C14.3874 2.02072 14.7259 2.05337 15.0354 2.1363C16.4157 2.50617 17.4939 3.58436 17.8638 4.96473C18.0009 5.47638 18.0006 6.06704 18.0001 6.86243C18.0001 6.90762 18.0001 6.95347 18.0001 7.00001V16.2413C18.0001 17.0463 18.0001 17.7106 17.9559 18.2518C17.91 18.8139 17.8114 19.3306 17.5641 19.816C17.1806 20.5686 16.5687 21.1805 15.8161 21.564C15.3307 21.8113 14.814 21.9099 14.2519 21.9558C13.7107 22 13.0464 22 12.2414 22H5.75877C4.95381 22 4.28946 22 3.74827 21.9558C3.18617 21.9099 2.66947 21.8113 2.18413 21.564C1.43149 21.1805 0.819564 20.5686 0.436071 19.816C0.18878 19.3306 0.0902153 18.8139 0.0442901 18.2518C7.27288e-05 17.7106 8.32179e-05 17.0463 9.59733e-05 16.2413L9.64501e-05 7.00001C9.64501e-05 6.95347 7.16546e-05 6.90762 4.72167e-05 6.86242C-0.000383486 6.06704 -0.00070332 5.47638 0.136393 4.96473C0.506262 3.58436 1.58445 2.50617 2.96482 2.1363C3.27434 2.05337 3.61277 2.02072 4.00794 2.00796C4.01024 1.95756 4.01321 1.90871 4.01704 1.86178C4.03572 1.63318 4.07979 1.36345 4.21808 1.09202C4.40983 0.7157 4.71579 0.409739 5.09212 0.217992C5.36354 0.0796948 5.63327 0.0356272 5.86187 0.0169504C6.07002 -5.6861e-05 6.31582 -2.74161e-05 6.5682 2.50544e-06ZM4.00877 4.00956C3.74568 4.01867 3.60258 4.03597 3.48246 4.06815C2.79227 4.25309 2.25318 4.79218 2.06824 5.48237C2.00869 5.70465 2.0001 6.00565 2.0001 7.00001V16.2C2.0001 17.0566 2.00087 17.6389 2.03765 18.089C2.07347 18.5274 2.13839 18.7516 2.21808 18.908C2.40983 19.2843 2.71579 19.5903 3.09212 19.782C3.24852 19.8617 3.47272 19.9266 3.91113 19.9625C4.36122 19.9992 4.94352 20 5.8001 20H12.2001C13.0567 20 13.639 19.9992 14.0891 19.9625C14.5275 19.9266 14.7517 19.8617 14.9081 19.782C15.2844 19.5903 15.5904 19.2843 15.7821 18.908C15.8618 18.7516 15.9267 18.5274 15.9625 18.089C15.9993 17.6389 16.0001 17.0566 16.0001 16.2V7.00001C16.0001 6.00565 15.9915 5.70465 15.9319 5.48237C15.747 4.79218 15.2079 4.25309 14.5177 4.06815C14.3976 4.03597 14.2545 4.01867 13.9914 4.00956C13.9893 4.05378 13.9865 4.09676 13.9832 4.13824C13.9645 4.36683 13.9204 4.63656 13.7821 4.90799C13.5904 5.28431 13.2844 5.59027 12.9081 5.78202C12.6367 5.92032 12.3669 5.96438 12.1383 5.98306C11.9302 6.00007 11.6844 6.00004 11.432 6.00001L6.6001 6.00001C6.58945 6.00001 6.57882 6.00001 6.5682 6.00001C6.31582 6.00004 6.07002 6.00007 5.86187 5.98306C5.63327 5.96438 5.36354 5.92032 5.09212 5.78202C4.71579 5.59027 4.40983 5.28431 4.21808 4.90799C4.07979 4.63656 4.03572 4.36683 4.01704 4.13824C4.01365 4.09676 4.01094 4.05378 4.00877 4.00956ZM12.0001 2.60001C12.0001 2.30348 11.9993 2.14122 11.9898 2.02464C11.9894 2.02 11.989 2.0156 11.9886 2.01145C11.9845 2.01107 11.9801 2.01069 11.9755 2.01031C11.8589 2.00078 11.6966 2.00001 11.4001 2.00001H6.6001C6.30357 2.00001 6.14132 2.00078 6.02473 2.01031C6.02009 2.01069 6.0157 2.01107 6.01154 2.01145C6.01116 2.0156 6.01078 2.02 6.0104 2.02464C6.00087 2.14122 6.0001 2.30348 6.0001 2.60001V3.40001C6.0001 3.69653 6.00087 3.85879 6.0104 3.97537C6.01078 3.98001 6.01116 3.98441 6.01154 3.98856C6.0157 3.98894 6.02009 3.98932 6.02473 3.9897C6.14131 3.99923 6.30357 4.00001 6.6001 4.00001H11.4001C11.6966 4.00001 11.8589 3.99923 11.9755 3.9897C11.9801 3.98932 11.9845 3.98894 11.9886 3.98856C11.989 3.98441 11.9894 3.98001 11.9898 3.97537C11.9993 3.85879 12.0001 3.69653 12.0001 3.40001V2.60001Z"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.0001 12C4.0001 11.4477 4.44781 11 5.0001 11H13.0001C13.5524 11 14.0001 11.4477 14.0001 12C14.0001 12.5523 13.5524 13 13.0001 13H5.0001C4.44781 13 4.0001 12.5523 4.0001 12ZM4.0001 9C4.0001 8.44772 4.44781 8 5.0001 8H13.0001C13.5524 8 14.0001 8.44772 14.0001 9C14.0001 9.55229 13.5524 10 13.0001 10H5.0001C4.44781 10 4.0001 9.55229 4.0001 9ZM4.0001 15C4.0001 14.4477 4.44781 14 5.0001 14H13.0001C13.5524 14 14.0001 14.4477 14.0001 15C14.0001 15.5523 13.5524 16 13.0001 16H5.0001C4.44781 16 4.0001 15.5523 4.0001 15Z"
      />
    </g>
  </svg>
)

const IncomeIcon = () => (
  <svg viewBox="0 0 22 22" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.00001 4.5C6.00001 2.01472 8.01472 0 10.5 0C11.7661 0 12.9095 0.523286 13.7263 1.36307C14.2705 1.1294 14.8702 1 15.5 1C17.9853 1 20 3.01472 20 5.5C20 7.98528 17.9853 10 15.5 10C14.2345 10 13.0911 9.47764 12.2734 8.63677C11.7288 8.87051 11.1289 9 10.5 9C8.01472 9 6.00001 6.98528 6.00001 4.5ZM11.2204 6.89492C11.0773 6.45575 11 5.98691 11 5.5C11 4.37981 11.4093 3.35522 12.0865 2.56763C11.6544 2.21233 11.1018 2 10.5 2C9.11929 2 8 3.11929 8 4.5C8 5.88071 9.11929 7 10.5 7C10.7509 7 10.9926 6.96328 11.2204 6.89492ZM15.5 3C14.1193 3 13 4.11929 13 5.5C13 6.88071 14.1193 8 15.5 8C16.8807 8 18 6.88071 18 5.5C18 4.11929 16.8807 3 15.5 3ZM2.56809 11L3.43189 11C3.68428 11 3.93007 10.9999 4.13824 11.0169C4.36683 11.0356 4.63656 11.0797 4.90799 11.218C5.28431 11.4097 5.59027 11.7157 5.78202 12.092C5.861 12.247 5.90925 12.4015 5.93958 12.548L7.53285 11.8818L7.54205 11.8776C7.55726 11.8708 7.57881 11.8612 7.60606 11.8492C7.66053 11.8252 7.73803 11.7916 7.83354 11.7516C8.02386 11.6719 8.28913 11.5653 8.58833 11.458C9.14131 11.2599 9.96776 11 10.6431 11C11.1223 11 11.599 11.0571 12.0643 11.1699L14.4497 11.75C15.5433 12.0152 16.3491 12.9573 16.4048 14.0844L17.8878 13.0025C18.9068 12.2594 20.3291 12.3548 21.2352 13.2363C22.2544 14.2277 22.2555 15.847 21.2352 16.8385L19.0776 18.9373C18.3555 19.6397 17.4379 20.1161 16.4442 20.3097L13.3945 20.903C12.6488 21.0478 11.8793 21.0305 11.1402 20.8505L8.38268 20.1804L8.3804 20.1798C8.12936 20.1182 7.87118 20.0872 7.6103 20.0872H5.98685C5.98569 20.1044 5.98443 20.1214 5.98306 20.1382C5.96438 20.3668 5.92032 20.6366 5.78202 20.908C5.59027 21.2843 5.28431 21.5903 4.90799 21.782C4.63656 21.9203 4.36683 21.9644 4.13824 21.9831C3.93008 22.0001 3.6843 22 3.43192 22H2.56816C2.31576 22 2.06995 22.0001 1.86177 21.9831C1.63318 21.9644 1.36345 21.9203 1.09202 21.782C0.715701 21.5903 0.409739 21.2843 0.217992 20.908C0.0796948 20.6366 0.0356272 20.3668 0.0169504 20.1382C-5.6861e-05 19.9301 -2.74161e-05 19.6843 2.50544e-06 19.4319L2.62465e-06 13.5681C-2.74161e-05 13.3157 -5.67418e-05 13.0699 0.0169504 12.8618C0.0356273 12.6332 0.0796949 12.3634 0.217992 12.092C0.409739 11.7157 0.715699 11.4097 1.09202 11.218C1.36345 11.0797 1.63318 11.0356 1.86178 11.0169C2.06993 10.9999 2.31571 11 2.56809 11ZM6.00001 18.0872H7.6103C8.02964 18.0872 8.44753 18.137 8.85593 18.2372L8.85723 18.2375L11.613 18.9072C12.0708 19.0187 12.5488 19.0298 13.0125 18.9398L16.0618 18.3466C16.6791 18.2263 17.2437 17.9311 17.6831 17.5037L19.8406 15.4049C20.052 15.1996 20.0535 14.8769 19.8406 14.6699C19.6373 14.472 19.3025 14.4461 19.0663 14.6183L16.5527 16.4522C16.0188 16.8424 15.3733 17.0498 14.7137 17.0498L12.2855 17.0498C11.7333 17.0498 11.2855 16.6021 11.2855 16.0499C11.2855 15.4976 11.7332 15.0499 12.2855 15.0498L13.831 15.0498C14.1761 15.0498 14.4079 14.7852 14.4079 14.5159V14.2091C14.4079 13.9836 14.2478 13.759 13.9784 13.6937L11.5929 13.1136C11.2818 13.0381 10.9633 13 10.6431 13C10.3537 13 9.82455 13.1396 9.2631 13.3408C9.0051 13.4332 8.77346 13.5263 8.60617 13.5964C8.52287 13.6313 8.4564 13.6601 8.41159 13.6798L8.36145 13.7021L8.34984 13.7073L8.34785 13.7082L8.31771 13.7214L6.00001 14.6906V18.0872ZM4.00001 14.048V19.4C4.00001 19.6965 3.99923 19.8588 3.9897 19.9754C3.98932 19.98 3.98894 19.9844 3.98856 19.9885C3.98441 19.9889 3.98001 19.9893 3.97537 19.9897C3.85879 19.9992 3.69653 20 3.4 20H2.6C2.30348 20 2.14123 19.9992 2.02464 19.9897C2.02 19.9893 2.0156 19.9889 2.01145 19.9885C2.01107 19.9844 2.01069 19.98 2.01031 19.9754C2.00078 19.8588 2.00001 19.6965 2.00001 19.4V13.6C2.00001 13.3035 2.00078 13.1412 2.01031 13.0246C2.01069 13.02 2.01107 13.0156 2.01145 13.0114C2.0156 13.0111 2.02 13.0107 2.02464 13.0103C2.14122 13.0008 2.30348 13 2.60001 13L3.40001 13C3.69653 13 3.85879 13.0008 3.97537 13.0103C3.98001 13.0107 3.98441 13.0111 3.98856 13.0114C3.98894 13.0156 3.98932 13.02 3.9897 13.0246C3.99923 13.1412 4.00001 13.3035 4.00001 13.6V14.0026C3.99966 14.0177 3.99966 14.0328 4.00001 14.048Z"
    />
  </svg>
)

const MarketingIcon = () => (
  <svg viewBox="0 0 22 21.1155" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.3895 2.07979C16.1492 2.18819 15.824 2.36471 15.3229 2.63784C13.8504 3.44054 11.8498 4.36104 10 4.57121V11.6597C11.8498 11.8699 13.8504 12.7904 15.3229 13.5931C15.824 13.8662 16.1492 14.0427 16.3895 14.1511C16.4214 14.1655 16.4492 14.1775 16.4733 14.1873C16.4755 14.1668 16.4777 14.1436 16.4798 14.1174C16.4992 13.8729 16.5 13.5289 16.5 12.9892V3.24175C16.5 2.70206 16.4992 2.35806 16.4798 2.11351C16.4777 2.08732 16.4755 2.06409 16.4733 2.04358C16.4492 2.05347 16.4214 2.0654 16.3895 2.07979ZM18.5 3.72697V3.2016C18.5 2.71385 18.5 2.28882 18.4735 1.9551C18.4484 1.63937 18.3906 1.20154 18.1266 0.832534C17.7906 0.362822 17.3164 0.0812235 16.7431 0.0110132C16.2851 -0.0450888 15.8593 0.124831 15.5669 0.25676C15.2493 0.400075 14.857 0.613971 14.4002 0.862951L14.3657 0.881806C12.7058 1.78666 10.7411 2.61547 9.25 2.61547H5.5C2.46243 2.61547 -1.90735e-06 5.0779 0 8.11547C1.07288e-06 9.84088 0.794512 11.3807 2.03764 12.3891L3.6045 18.6566L3.61232 18.6879C3.64879 18.8339 3.68044 18.9607 3.72013 19.0779C4.10397 20.2111 5.1245 21.0079 6.31701 21.1055C6.4403 21.1156 6.571 21.1155 6.72156 21.1155H6.75379H6.79509C6.98251 21.1155 7.14362 21.1156 7.2896 21.1015C8.71748 20.963 9.8475 19.833 9.98599 18.4051C10.0002 18.2591 10.0001 18.098 10 17.9106V17.8693V13.6762C11.3629 13.883 12.9717 14.5893 14.3657 15.3491L14.4002 15.368C14.8569 15.617 15.2493 15.8309 15.5669 15.9742C15.8593 16.1061 16.2851 16.276 16.7431 16.2199C17.3164 16.1497 17.7906 15.8681 18.1266 15.3984C18.3906 15.0294 18.4484 14.5916 18.4735 14.2758C18.5 13.9421 18.5 13.5171 18.5 13.0293V12.504C20.504 12.0492 22 10.2571 22 8.11547C22 5.97388 20.504 4.1817 18.5 3.72697ZM18.5 5.82349V10.4074C19.383 10.0217 20 9.14063 20 8.11547C20 7.09031 19.383 6.20927 18.5 5.82349ZM8 11.6155V4.61547H5.5C3.567 4.61547 2 6.18247 2 8.11547C2 10.0485 3.567 11.6155 5.5 11.6155H8ZM8 13.6155V17.8693C8 18.1213 7.99883 18.176 7.99533 18.212C7.94917 18.688 7.5725 19.0646 7.09654 19.1108C7.06053 19.1143 7.00587 19.1155 6.75379 19.1155C6.55288 19.1155 6.51079 19.1147 6.48004 19.1121C6.08254 19.0796 5.74236 18.814 5.61441 18.4363C5.60452 18.4071 5.59352 18.3664 5.54479 18.1715L4.3771 13.5007C4.73961 13.5759 5.11519 13.6155 5.5 13.6155H8Z"
    />
  </svg>
)

const SettingsIcon = () => (
  <svg viewBox="0 0 22 22" aria-hidden="true">
    <g>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11 9C9.89543 9 9 9.89543 9 11C9 12.1046 9.89543 13 11 13C12.1046 13 13 12.1046 13 11C13 9.89543 12.1046 9 11 9ZM7 11C7 8.79086 8.79086 7 11 7C13.2091 7 15 8.79086 15 11C15 13.2091 13.2091 15 11 15C8.79086 15 7 13.2091 7 11Z"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.16468 2.27573C8.75303 -0.758576 13.247 -0.758575 13.8353 2.27573C13.9447 2.83955 14.6258 3.10931 15.1159 2.7743C17.6841 1.01895 20.9564 3.94086 19.2426 6.55397L19.0468 6.85259C18.7114 7.36395 19.0081 8.04913 19.6283 8.17446C22.7906 8.81348 22.7906 13.1865 19.6283 13.8255C19.0081 13.9509 18.7114 14.636 19.0468 15.1474L19.2426 15.446C20.9564 18.0591 17.6841 20.981 15.1159 19.2257C14.6258 18.8907 13.9447 19.1604 13.8353 19.7243C13.247 22.7586 8.75303 22.7586 8.16468 19.7243C8.05535 19.1604 7.37423 18.8907 6.88408 19.2257C4.3159 20.981 1.04363 18.0591 2.7574 15.446L2.95324 15.1474C3.28861 14.636 2.99189 13.9509 2.37166 13.8255C-0.790553 13.1865 -0.790555 8.81348 2.37166 8.17446C2.99189 8.04913 3.28861 7.36395 2.95324 6.85259L2.7574 6.55397C1.04363 3.94085 4.3159 1.01895 6.88408 2.7743C7.37423 3.10931 8.05535 2.83955 8.16468 2.27573ZM11.8048 2.64399C11.6378 1.78271 10.3622 1.78271 10.1952 2.64399C9.8071 4.6455 7.40552 5.57733 5.69176 4.40598C4.92717 3.88338 4.02983 4.76387 4.50197 5.48378L4.69781 5.7824C5.81003 7.47828 4.81945 9.721 2.7942 10.1303C1.82315 10.3265 1.82315 11.6735 2.7942 11.8697C4.81945 12.279 5.81004 14.5217 4.69782 16.2176L4.50197 16.5162C4.02983 17.2361 4.92717 18.1166 5.69177 17.594C7.40552 16.4227 9.8071 17.3545 10.1952 19.356C10.3622 20.2173 11.6378 20.2173 11.8048 19.356C12.1929 17.3545 14.5945 16.4227 16.3082 17.594C17.0728 18.1166 17.9702 17.2361 17.498 16.5162L17.3022 16.2176C16.19 14.5217 17.1805 12.279 19.2058 11.8697C20.1769 11.6735 20.1769 10.3265 19.2058 10.1303C17.1806 9.721 16.19 7.47828 17.3022 5.7824L17.498 5.48378C17.9702 4.76387 17.0728 3.88338 16.3082 4.40598C14.5945 5.57733 12.1929 4.6455 11.8048 2.64399Z"
      />
    </g>
  </svg>
)

const Sidebar = () => {
  const { t } = useTranslation()

  const storeName = useSelector(selectStoreName)
  const storeId = useSelector(selectStoreId)
  const storeAvatar = useSelector(selectStoreAvatar)
  const storeAvailability = useSelector(selectStoreAvailability)
  const isStoreUsingCashCo = useSelector(selectStoreCashCoStatus)
  const storeType = useSelector(selectStoreType)
  const { data: branches } = useFetchStoreBranchesQuery()
  const location = useLocation()
  const {
    isRbacEnabled,
    canViewPerformance,
    canViewBilling,
    canViewMenu,
    canManageOffers,
    canViewBusinessHours
  } = useAccessControl()

  const {
    lmd_store_webapp_FR_Billing,
    lmd_store_webapp_offers_page,
    lmd_store_web_offers_marketing,
    store_web_app_branches_management,
    store_web_app_busy_mode
  } = useFlags()
  const phoneCode = useSelector(selectPhoneCode)

  const { data: ordersCounts } = useGetOrdersCountByTypesQuery({
    store_id: storeId as string
  })

  const dispatch = useDispatch()
  const isSidebarOpen = useSelector(selectIsSidebarOpen)
  const onCloseSidebar = useCallback(() => {
    dispatch(setSideBarOpen(false))
  }, [dispatch])

  const { isMobile } = useDeviceSize(950)
  const isFirstDesktopRenderRef = useRef(true)
  const wasMobileRef = useRef(isMobile)
  const isCollapsedDesktop = !isMobile && !isSidebarOpen

  const isMarketingRoute = lmd_store_web_offers_marketing
    ? location.pathname.startsWith(PATHS.MARKETING_TOOLS) ||
      location.pathname.startsWith(PATHS.MARKETING_CAMPAIGNS) ||
      location.pathname.startsWith(PATHS.OFFERS)
    : location.pathname.startsWith(PATHS.OFFERS)

  const [isMarketingOpen, setIsMarketingOpen] = useState(isMarketingRoute)

  useEffect(() => {
    if (isMobile) {
      onCloseSidebar()
      wasMobileRef.current = true
      return
    }

    if (isFirstDesktopRenderRef.current || wasMobileRef.current) {
      dispatch(setSideBarOpen(true))
      isFirstDesktopRenderRef.current = false
      wasMobileRef.current = false
    }
  }, [dispatch, isMobile, onCloseSidebar])

  useEffect(() => {
    if (isMarketingRoute && !isCollapsedDesktop) {
      setIsMarketingOpen(true)
    }
  }, [isMarketingRoute, isCollapsedDesktop])

  const [changeStoreAvailability, { isLoading }] =
    useChangeStoreAvailabilityMutation()

  const { data: busyStatusData } = useGetStoreBusyStatusQuery(undefined, {
    pollingInterval: 10000
  })
  const [isOnlineRestrictionOpen, setIsOnlineRestrictionOpen] = useState(false)

  useEffect(() => {
    dispatch(setAutoBusy(busyStatusData?.isPostRejection || false))
  }, [dispatch, busyStatusData?.isPostRejection])

  const logAvailabilityEvent = (eventName: string) => {
    logFirebaseEvent(eventName, {
      Event_Time: format(new Date(), 'dd/MM/yyyy - HH:mm:ss'),
      Store_ID: storeId
    })
  }

  const onSwitchToggle = (newStatus?: 0 | 1 | 2) => {
    if (!storeId || typeof storeAvailability !== 'number') return
    if (newStatus === undefined || newStatus === BUSY_STATUS.BUSY) return
    if (newStatus === STORE_AVAILABILITY.OFFLINE && storeAvailability === 0) {
      return
    }
    if (newStatus === STORE_AVAILABILITY.ONLINE && storeAvailability === 1) {
      return
    }

    const availability = newStatus === STORE_AVAILABILITY.ONLINE ? 1 : 0
    const eventName =
      availability === 1 ? 'AC_Web_Toggle_On' : 'AC_Web_Toggle_Off'

    changeStoreAvailability({
      availability,
      store_id: storeId
    })
      .unwrap()
      .then(() => {
        logAvailabilityEvent(eventName)
      })
      .catch(error => {
        if (
          availability === STORE_AVAILABILITY.ONLINE &&
          error?.errorCode === 'OPENING_RESTRICTED'
        ) {
          setIsOnlineRestrictionOpen(false)
          setTimeout(() => {
            setIsOnlineRestrictionOpen(true)
          }, 0)
        }
      })
  }

  const onNavItemClick = () => {
    if (isMobile) {
      onCloseSidebar()
    }
  }

  const pages = useMemo(
    () => [
      {
        url: PATHS.ORDERS,
        icon: <OrdersIcon />,
        name: t('common.page-names.orders'),
        notificationComponent: (
          <NotificationBell
            className={Styles.newOrderBell}
            count={ordersCounts?.new || 0}
          />
        ),
        hidden: false,
        showChevron: false
      },
      {
        url: PATHS.PERFORMANCE,
        icon: <PerformanceIcon />,
        name: t('common.page-names.performance'),
        notificationComponent: null,
        hidden: !canViewPerformance,
        showChevron: false
      },
      {
        url: PATHS.MENU,
        icon: <MenuIcon />,
        name: t('common.page-names.menu'),
        notificationComponent: null,
        hidden: !canViewMenu,
        showChevron: false
      },
      {
        url: PATHS.BILLING,
        icon: <IncomeIcon />,
        name: t('common.page-names.income'),
        notificationComponent: null,
        hidden:
          !canViewBilling ||
          (!isRbacEnabled &&
            (!isStoreUsingCashCo ||
              (!lmd_store_webapp_FR_Billing && phoneCode === '+33'))),
        showChevron: false
      },
      {
        url: PATHS.OFFERS,
        icon: <MarketingIcon />,
        name: lmd_store_web_offers_marketing
          ? t('common.page-names.marketing')
          : t('common.page-names.offers'),
        notificationComponent: null,
        hidden:
          !canManageOffers ||
          (!isRbacEnabled &&
            (!lmd_store_webapp_offers_page || storeType === 'dark_store')),
        showChevron: lmd_store_web_offers_marketing
      },
      {
        url: PATHS.ACCOUNT,
        icon: <SettingsIcon />,
        name: t('common.page-names.settings'),
        notificationComponent: null,
        hidden: false,
        showChevron: true
      }
    ],
    [
      t,
      isStoreUsingCashCo,
      ordersCounts,
      isRbacEnabled,
      canViewPerformance,
      canViewBilling,
      canViewMenu,
      canManageOffers,
      lmd_store_webapp_FR_Billing,
      phoneCode,
      lmd_store_webapp_offers_page,
      lmd_store_web_offers_marketing,
      storeType
    ]
  )

  const marketingItems = useMemo(() => {
    if (!lmd_store_web_offers_marketing) return []

    return [
      {
        id: 'tools',
        label: t('marketing.tools'),
        url: PATHS.MARKETING_TOOLS
      },
      {
        id: 'campaigns',
        label: t('marketing.campaigns.label'),
        url: PATHS.MARKETING_CAMPAIGNS
      }
    ]
  }, [lmd_store_web_offers_marketing, t])

  return (
    <>
      <div
        className={clsx(Styles.sidebar, {
          [Styles.open]: isSidebarOpen,
          [Styles.collapsedDesktop]: isCollapsedDesktop
        })}>
        <ScrollArea>
          <div
            className={clsx(Styles.sidebarContent, {
              [Styles.sidebarContentCollapsed]: isCollapsedDesktop
            })}>
            {isSidebarOpen && isMobile && (
              <ArrowLeft
                width={31}
                height={31}
                onClick={onCloseSidebar}
                className={Styles.mobileCloseButton}
              />
            )}
            {isCollapsedDesktop ? (
              <div className={Styles.collapsedTopSection}>
                <div className={Styles.collapsedAvatarContainer}>
                  <Avatar
                    size="medium"
                    string={storeName || ''}
                    image={storeAvatar || ''}
                    className={Styles.storeAvatar}
                  />
                  <span
                    className={clsx(Styles.collapsedOnlineIndicator, {
                      [Styles.collapsedOnlineIndicatorOffline]:
                        storeAvailability === STORE_AVAILABILITY.OFFLINE
                    })}
                  />
                </div>
              </div>
            ) : (
              <div className={Styles.storeCardWrapper}>
                <div className={Styles.storeCard}>
                  <div className={Styles.storeCardHeader}>
                    <div className={Styles.storeIdentity}>
                      <Avatar
                        size="medium"
                        string={storeName || ''}
                        image={storeAvatar || ''}
                        className={Styles.storeAvatar}
                      />
                      <div className={Styles.storeInfo}>
                        <Text className={Styles.storeLabel} asChild>
                          <p>{t('common.sidebar.store-label')}</p>
                        </Text>
                        <Text className={Styles.storeName} asChild>
                          <p>{storeName}</p>
                        </Text>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={Styles.storeSettingsButton}
                      aria-label={t('common.sidebar.store-settings')}
                      disabled>
                      <span
                        className={clsx(
                          Styles.figmaSmallIconWrapper,
                          Styles.quickSettingsIcon
                        )}>
                        <img src={FIGMA_QUICK_SETTINGS_ICON} alt="" />
                      </span>
                    </button>
                  </div>
                  {(store_web_app_busy_mode ||
                    import.meta.env.DEV) ? (
                    <SwitchWithWordsWithModal
                      onToggle={status => {
                        if (status !== undefined) {
                          onSwitchToggle(status)
                        }
                      }}
                      status={
                        // Always respect the actual store availability first, regardless of flag changes
                        storeAvailability === 0
                          ? STORE_AVAILABILITY.OFFLINE
                          : busyStatusData?.isBusy
                          ? BUSY_STATUS.BUSY
                          : BUSY_STATUS.NON_BUSY
                      }
                      isLoading={isLoading}
                      className={Styles.storeStatusSwitch}
                      storeId={storeId as string}
                      width={256}
                    />
                  ) : (
                    <SwitchWithWords
                      onToggle={(value?: boolean) => {
                        if (value !== undefined) {
                          const newStatus = value
                            ? STORE_AVAILABILITY.ONLINE
                            : STORE_AVAILABILITY.OFFLINE
                          onSwitchToggle(newStatus)
                        }
                      }}
                      checked={storeAvailability === 1}
                      isLoading={isLoading}
                      className={Styles.storeStatusSwitch}
                      checkedWord={t('switch-with-words.statuses.accepting')}
                      uncheckedWord={t('switch-with-words.statuses.offline')}
                      width={256}
                    />
                  )}
                </div>
              </div>
            )}
            {store_web_app_branches_management &&
              canViewBusinessHours &&
              !isCollapsedDesktop &&
              Array.isArray(branches) &&
              branches.length > 0 && (
                <Link to="/branch/business-hours" className={Styles.branchLink}>
                  <Button
                    variant="transparentOutlined"
                    icon={<Timer />}
                    size="large"
                    loading={isLoading}
                    shape="rounded"
                    className={Styles.branchButton}>
                    {t('common.sidebar.manage-branch-availability')}
                  </Button>
                </Link>
              )}
            <div className={Styles.links}>
              {pages.map(
                (
                  {
                    name,
                    url,
                    icon,
                    notificationComponent,
                    hidden,
                    showChevron
                  },
                  index
                ) => {
                  if (hidden) return null
                  if (
                    url === PATHS.OFFERS &&
                    lmd_store_web_offers_marketing &&
                    !isCollapsedDesktop
                  ) {
                    return (
                      <div
                        className={clsx(Styles.marketingGroup, {
                          [Styles.marketingGroupOpen]: isMarketingOpen
                        })}
                        key={index}>
                        <button
                          type="button"
                          className={clsx(
                            Styles.link,
                            Styles.linkWithChevron,
                            Styles.marketingTrigger,
                            {
                              [Styles.activeLink]: isMarketingRoute
                            }
                          )}
                          onClick={() =>
                            setIsMarketingOpen(current => !current)
                          }
                          aria-expanded={isMarketingOpen}>
                          <span className={Styles.figmaIconWrapper}>{icon}</span>
                          <span className={Styles.linkLabel}>{name}</span>
                          <span
                            className={clsx(
                              Styles.figmaChevronIcon,
                              Styles.marketingChevron,
                              {
                                [Styles.marketingChevronOpen]: isMarketingOpen
                              }
                            )}>
                            <img src={FIGMA_CHEVRON_ICON} alt="" />
                          </span>
                        </button>
                        {isMarketingOpen && (
                          <div className={Styles.marketingMenu}>
                            {marketingItems.map(item => (
                              <NavLink
                                key={item.id}
                                to={item.url}
                                onClick={onNavItemClick}
                                isActive={match => {
                                  if (match) return true
                                  if (item.id === 'campaigns') {
                                    return location.pathname.startsWith(
                                      PATHS.MARKETING_CAMPAIGNS
                                    )
                                  }
                                  return location.pathname.startsWith(
                                    PATHS.MARKETING_TOOLS
                                  )
                                }}
                                activeClassName={Styles.subLinkActive}
                                className={Styles.subLink}>
                                <span className={Styles.subLinkDot} />
                                <span className={Styles.subLinkLabel}>
                                  {item.label}
                                </span>
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  }
                  return (
                    <NavLink
                      key={index}
                      activeClassName={
                        isCollapsedDesktop
                          ? Styles.activeLinkCollapsed
                          : Styles.activeLink
                      }
                      onClick={onNavItemClick}
                      className={clsx(Styles.link, {
                        [Styles.linkWithChevron]:
                          showChevron && !isCollapsedDesktop,
                        [Styles.linkCollapsed]: isCollapsedDesktop
                      })}
                      to={url}>
                      <span className={Styles.figmaIconWrapper}>{icon}</span>
                      {!isCollapsedDesktop && (
                        <span className={Styles.linkLabel}>{name}</span>
                      )}
                      {!isCollapsedDesktop && notificationComponent}
                      {showChevron && !isCollapsedDesktop && (
                        <span className={Styles.figmaChevronIcon}>
                          <img src={FIGMA_CHEVRON_ICON} alt="" />
                        </span>
                      )}
                    </NavLink>
                  )
                }
              )}
            </div>
            {isCollapsedDesktop ? (
              <button
                type="button"
                className={Styles.collapsedToggleButton}
                aria-label={t('common.sidebar.expand')}
                onClick={() => {
                  dispatch(setSideBarOpen(true))
                }}>
                <span className={Styles.collapsedToggleIcon}>
                  <img src={FIGMA_COLLAPSED_TOGGLE_ICON} alt="" />
                </span>
              </button>
            ) : (
              <Button
                variant="transparentOutlined"
                icon={<ArrowLeft width={14} height={14} />}
                size="small"
                shape="rounded"
                className={Styles.collapseButton}
                onClick={onCloseSidebar}>
                {t('common.sidebar.collapse')}
              </Button>
            )}
          </div>
        </ScrollArea>
      </div>
      {isMobile && isSidebarOpen && (
        <div
          data-testid="sidebar-overlay"
          onClick={onCloseSidebar}
          className={Styles.overlay}
        />
      )}
      {isOnlineRestrictionOpen && (
        <OnlineRestriction onClose={() => setIsOnlineRestrictionOpen(false)}>
          <div />
        </OnlineRestriction>
      )}
    </>
  )
}

export default Sidebar
